import { useEffect, useState, useRef } from 'react'
import React from 'react'
import Konva from 'konva'
import { Maximize, X } from 'lucide-react'
import { useYard } from '../contexts/YardContext'
import { useAlerts } from '../contexts/AlertContext'
import { useWebSocketContext } from '../contexts/WebSocketContext'
import { apiClient } from '@nyos/shared'
import { GisLayoutOrientation } from '../types/interchange'
import { 
  calculateBoundingBox, 
  projectToCanvas,
  canvasToCoordinates,
  applyOrientationTransform, 
  interpolateTrackPosition,
  initializeCameraState,
  MapViewState,
  CanvasOffset
} from '../utils/yardMapUtils'
import YardMapCanvas from './YardMapCanvas'
import { useStageSize } from '../hooks/useStageSize'

interface GeoJSONFeatureCollection {
  type: string
  features: any[]
  metadata?: {
    facilityId: string
    yardName: string
    gisLayoutOrientation: string
  }
}

interface RailCarPosition {
  railCarId: string
  reportingMark: string
  latitude: number
  longitude: number
  timestamp: number
  status?: string
  lengthFeet?: number
  assetType?: string
  currentTrackId?: string
  linearOffset?: number
  snapConfidence?: number
}

interface YardAsset {
  id: string
  reportingMark: string
  assetType: string
  state: string
  lengthFeet?: number
  kinematics?: {
    position?: {
      longitude: number
      latitude: number
    }
    velocity?: number
    heading?: number
    currentTrackId?: string
    linearOffset?: number
  }
}

/**
 * Imperative camera target for "Locate on Map" (header search).
 * - track: frame the track's bounding box.
 * - car:   center on the car's interpolated on-track point at the
 *          track-fit zoom — never falls back to the full-facility bbox
 *          for a single car.
 * Bump `requestId` to re-fire focus on the same target.
 */
export interface MapFocusTarget {
  kind: 'track' | 'car'
  trackId: string
  /** Feet along the track; required for kind 'car'. */
  linearOffset?: number
  /** Human-readable name for the focus chip (track name, reporting
   *  mark, switch name) — chips must never show the raw UUID. */
  label?: string
  requestId: number
}

interface YardMapProps {
  onTrackClick?: (trackId: string) => void
  activeSweepAudits?: string[]
  activeTheme: 'midnight' | 'foundry' | 'daylight' | 'cyber'
  focusTarget?: MapFocusTarget | null
  /** Clears the caller's focus state when the user exits focused view. */
  onFocusClear?: () => void
}

const YardMap = ({ onTrackClick, activeSweepAudits = [], activeTheme, focusTarget = null, onFocusClear }: YardMapProps) => {
  // FEATURE FLAG: Toggle to false to instantly kill camera interactions
  // and drop back to the stable Phase 1 static baseline.
  const ENABLE_CAMERA_INTERACTIONS = true

  // FEATURE FLAG: Toggle to false to use binary Macro/Micro threshold (150,000)
  // Toggle to true to use continuous non-linear scaling (squared curve)
  const ENABLE_CONTINUOUS_SCALING = true

  const { activeFacilityId } = useYard()
  const { alerts, addAlert, resolveAlert, clearAlerts } = useAlerts()
  const [yardOrientation, setYardOrientation] = useState<GisLayoutOrientation>('NORMAL')

  // Use refs to store latest function references to avoid WebSocket reconnection
  const addAlertRef = useRef(addAlert)
  const resolveAlertRef = useRef(resolveAlert)

  useEffect(() => {
    addAlertRef.current = addAlert
    resolveAlertRef.current = resolveAlert
  }, [addAlert, resolveAlert])


  const [geoData, setGeoData] = useState<GeoJSONFeatureCollection | null>(null)
  const stageSize = useStageSize({ widthFactor: 0.875, heightOffset: 64 })
  const [cameraState, setCameraState] = useState<MapViewState | null>(null)
  const [railCarPosition, setRailCarPosition] = useState<RailCarPosition | null>(null)
  
  // Canvas offset for panning - memoized for stable reference to prevent infinite loops
  const [canvasOffset, setCanvasOffset] = useState<CanvasOffset>({ x: 0, y: 0 })
  const [currentRailCarId, setCurrentRailCarId] = useState<string | null>(null)
  const [currentPos, setCurrentPos] = useState({ x: 0, y: 0, angle: 0 }) // Direct state-to-prop pipeline
  const currentRailCarIdRef = useRef<string | null>(null)
  
  // Pan state for mouse drag interactions
  const [isDragging, setIsDragging] = useState(false)
  const lastMousePos = useRef<{ x: number; y: number } | null>(null)
  
  // Zoom debouncing to prevent exponential compounding from rapid wheel events
  const lastZoomTime = useRef<number>(0)
  const ZOOM_DEBOUNCE_MS = 16 // ~60fps max zoom rate

  // TODO(tablet-refactor): touchGesture + handleTouchStart/Move/End +
  // suppressTapUntil are a ~130-line camera-gesture state machine. When the
  // mobile target needs pinch/pan, extract to
  // `frontend/shared/src/hooks/useTouchCameraGestures.ts` returning
  // { touchHandlers, suppressTapUntil } — it owns gesture mode, midpoint
  // anchoring, and tap suppression; callers pass camera/canvas setters.
  // Fold handleWheel's zoom math into the same hook so mouse and touch
  // semantics can't diverge.
  // Touch gesture state (one-finger pan / two-finger pinch). Refs, not
  // state — gesture coordinates mutate every frame and must not re-render.
  const touchGesture = useRef<{
    mode: 'none' | 'pan' | 'pinch'
    lastX: number
    lastY: number
    pinchDist: number
    moved: boolean
  }>({ mode: 'none', lastX: 0, lastY: 0, pinchDist: 0, moved: false })
  // Suppress the Konva tap (track select) that fires on touch-end after a
  // pan or pinch — without this, lifting fingers off a gesture selects a track.
  const suppressTapUntil = useRef(0)
  const [yardAssets, setYardAssets] = useState<YardAsset[]>([])
  const [hoveredCarId, setHoveredCarId] = useState<string | null>(null)
  const stageRef = useRef<Konva.Stage>(null)
  const railCarRef = useRef<Konva.Group>(null)
  const animationRef = useRef<Konva.Animation | null>(null)
  const activeTweenRef = useRef<Konva.Tween | null>(null)
  const isMapInitialized = useRef(false)
  const { subscribe } = useWebSocketContext()

  useEffect(() => {
    const fetchGeoData = async () => {
      if (!activeFacilityId) {
        setGeoData(null)
        setCameraState(null)
        return
      }

      try {
        const data = await apiClient.get<GeoJSONFeatureCollection>(`/api/v1/spatial/yard-map/${activeFacilityId}`)
        setGeoData(data)

        // Set yard orientation from API metadata
        if (data.metadata?.gisLayoutOrientation) {
          setYardOrientation(data.metadata.gisLayoutOrientation as GisLayoutOrientation)
        }

        // Initialize camera state when data loads (reset on yard change)
        if (data.features.length > 0) {
          // Calculate from bounding box and use as fixed static baseline
          const tracks = data.features.filter((f) => f.properties.type === 'track')
          const switches = data.features.filter((f) => f.properties.type === 'switch')
          const bbox = calculateBoundingBox([...tracks, ...switches])
          const initialCameraState = initializeCameraState(bbox, stageSize.width, stageSize.height)
          console.log('[INIT] Initial camera state:', initialCameraState)
          setCameraState(initialCameraState)
          isMapInitialized.current = true
        }

        // Clear alerts when yard changes
        clearAlerts()
      } catch (error) {
        console.error('Failed to fetch yard data:', error)
      }
    }

    // Reset initialization flag when yard changes
    isMapInitialized.current = false
    fetchGeoData()
  }, [activeFacilityId, clearAlerts])

  useEffect(() => {
    const fetchYardAssets = async () => {
      if (!activeFacilityId) {
        setYardAssets([])
        return
      }

      try {
        const assets = await apiClient.get<YardAsset[]>(`/api/v1/yards/${activeFacilityId}/assets`)
        setYardAssets(assets)
      } catch (error) {
        console.error('Failed to fetch yard assets:', error)
      }
    }

    fetchYardAssets()
  }, [activeFacilityId])

  useEffect(() => {
    const handleMessage = (message: any) => {
      // Check if the message is double-encoded
      let parsedData = message
      if (typeof message === 'string') {
        parsedData = JSON.parse(message)
      }
      
      const eventType = parsedData.eventType
      const messageType = parsedData.type
      
      // Handle SYSTEM_CLEAR message from backend
      if (messageType === 'SYSTEM_CLEAR') {
        setRailCarPosition(null)
        setCurrentRailCarId(null)
        return
      }
      
      // Handle FOUL_ZONE_ALERT message
      if (eventType === 'FOUL_ZONE_ALERT') {
        const alertData = parsedData.data
        if (alertData.assetId) {
          if (parsedData.resolved) {
            resolveAlertRef.current(alertData.assetId)
          } else {
            addAlertRef.current({
              id: parsedData.eventId,
              type: 'FOUL_ZONE_VIOLATION' as any,
              message: alertData.message,
              timestamp: parsedData.timestamp,
              resolved: parsedData.resolved,
              relatedEntityId: alertData.assetId,
              relatedEntityType: 'RailCar'
            })
          }
        }
        return
      }
      
      // Handle normal rail car movement
      const railCarData = parsedData as any
      
      // Debug logging to capture backend linearOffset stream
      console.debug('[RAILCAR WebSocket] carId:', railCarData.railCarId, 
                    'trackId:', railCarData.currentTrackId, 
                    'linearOffset:', railCarData.linearOffset,
                    'status:', railCarData.status)
      
      setRailCarPosition(railCarData)
    }

    const unsubscribe = subscribe(handleMessage)
    return () => {
      unsubscribe()
    }
  }, [subscribe])

  useEffect(() => {
    if (!railCarPosition || !railCarRef.current) return

    // Skip lat/lon tween animation if track-based positioning data is available
    // The new track-based positioning system handles movement directly
    if (railCarPosition.currentTrackId && railCarPosition.linearOffset !== undefined) {
      return
    }

    // Handle DEPARTED status - immediately stop active tween and remove car from stage
    if (railCarPosition.status === 'DEPARTED') {
      if (activeTweenRef.current) {
        activeTweenRef.current.destroy()
        activeTweenRef.current = null
      }
      if (animationRef.current) {
        animationRef.current.stop()
        animationRef.current = null
      }
      setRailCarPosition(null)
      setCurrentPos({ x: 0, y: 0, angle: 0 })
      currentRailCarIdRef.current = null
      setCurrentRailCarId(null)
      return
    }

    // Check if railCarId changed - new car
    const isNewCar = railCarPosition.railCarId !== currentRailCarId
    if (isNewCar) {
      setCurrentRailCarId(railCarPosition.railCarId)
      // Reset position for new car
      setCurrentPos({ x: 0, y: 0, angle: 0 })
    }

    const switches = geoData?.features.filter((f) => f.properties.type === 'switch') || []

    if (!cameraState) return

    // Find Switch 1 coordinate to cap tween destination
    const switch1 = switches.find(sw => sw.id === 'switch-1')
    const switch1Coord = switch1 ? (switch1.geometry.coordinates as number[]) : null
    const switch1Projected = switch1Coord ? projectToCanvas(switch1Coord[0], switch1Coord[1], cameraState, canvasOffset, stageSize.width, stageSize.height) : null

    const projected = projectToCanvas(
      railCarPosition.longitude,
      railCarPosition.latitude,
      cameraState,
      canvasOffset,
      stageSize.width,
      stageSize.height
    )

    const transformed = applyOrientationTransform(projected.x, projected.y, yardOrientation, stageSize.width)

    let targetX = transformed.x - 10
    let targetY = transformed.y - 10

    // Cap tween destination at Switch 1 coordinate to prevent overshoot
    if (switch1Projected && targetX > switch1Projected.x) {
      targetX = switch1Projected.x
      targetY = switch1Projected.y
    }

    // If new car, set initial position without animation
    if (isNewCar) {
      railCarRef.current.x(targetX)
      railCarRef.current.y(targetY)
      return
    }

    // Animate rail car to new position using Konva.Tween
    const tween = new Konva.Tween({
      node: railCarRef.current,
      x: targetX,
      y: targetY,
      duration: 2,
      easing: Konva.Easings.Linear,
      onFinish: () => {
        activeTweenRef.current = null
      }
    })

    activeTweenRef.current = tween
    tween.play()

    return () => {
      tween.destroy()
      activeTweenRef.current = null
    }
  }, [railCarPosition, geoData, stageSize])

  // Pulsing glow animation for foul zone alerts
  useEffect(() => {
    if (!railCarRef.current) return

    const hasAlert = railCarPosition && alerts.has(railCarPosition.railCarId)

    if (hasAlert) {
      // Start pulsing animation with increased intensity for dark theme visibility
      const anim = new Konva.Animation((frame) => {
        if (!railCarRef.current || !frame) return false
        const time = frame.time / 1000
        const pulse = Math.sin(time * Math.PI * 2) // 1 second cycle
        const shadowBlur = 30 + pulse * 20 // Oscillate between 30-50px (increased for dark theme)
        const colorIntensity = 0.8 + pulse * 0.2 // Oscillate between 0.8-1.0 (near full opacity)

        const rect = railCarRef.current.findOne('Rect') as Konva.Rect | undefined
        if (rect) {
          rect.shadowBlur(shadowBlur)
          rect.shadowColor(`rgba(255, 50, 50, ${colorIntensity})`) // Brighter red for dark theme
          rect.strokeEnabled(true)
          rect.stroke('#FF3333')
          rect.strokeWidth(2 + pulse * 2) // Pulsing stroke width
        }
      })

      animationRef.current = anim
      anim.start()
    } else {
      // Stop animation and reset shadow and stroke
      if (animationRef.current) {
        animationRef.current.stop()
        animationRef.current = null
      }
      const rect = railCarRef.current.findOne('Rect') as Konva.Rect | undefined
      if (rect) {
        rect.shadowBlur(0)
        rect.strokeEnabled(false)
      }
    }

    return () => {
      if (animationRef.current) {
        animationRef.current.stop()
        animationRef.current = null
      }
    }
  }, [alerts, railCarPosition])

  // Interpolate position between backend updates for smooth movement
  useEffect(() => {
    if (!railCarPosition || !railCarPosition.currentTrackId || railCarPosition.linearOffset === undefined) {
      return
    }

    const tracks = geoData?.features.filter((f) => f.properties.type === 'track') || []

    if (!cameraState) {
      return
    }

    const track = tracks.find(t => t.id === railCarPosition.currentTrackId)
    if (!track) {
      return
    }

    // Get track length for proper clamping
    const trackLengthFeet = track.properties?.lengthFeet || 2000

    // Normalize linear offset to track bounds without arbitrary amplification
    // Raw backend values (500, 600, 900) are already valid foot-offsets for a 2000-foot track
    const clampedLinearOffset = Math.max(0, Math.min(railCarPosition.linearOffset, trackLengthFeet))

    // Calculate target position from new backend data
    const targetCarPos = interpolateTrackPosition(
      track,
      clampedLinearOffset,
      cameraState,
      canvasOffset,
      stageSize.width,
      stageSize.height,
      yardOrientation
    )

    // If this is a new car or first update, set position immediately without animation
    const isNewCar = railCarPosition.railCarId !== currentRailCarIdRef.current
    const isFirstUpdate = currentPos.x === 0 && currentPos.y === 0
    
    if (isNewCar || isFirstUpdate) {
      // Update currentRailCarId so subsequent updates are not treated as new car
      if (isNewCar) {
        currentRailCarIdRef.current = railCarPosition.railCarId
        setCurrentRailCarId(railCarPosition.railCarId)
      }
      
      // Set position immediately
      setCurrentPos({ x: targetCarPos.x, y: targetCarPos.y, angle: targetCarPos.angle || 0 })
      
      // Update the actual Konva node immediately
      if (railCarRef.current) {
        railCarRef.current.x(targetCarPos.x)
        railCarRef.current.y(targetCarPos.y)
        railCarRef.current.rotation(targetCarPos.angle)
      }
      return
    }

    // For subsequent updates, update position directly
    setCurrentPos({ x: targetCarPos.x, y: targetCarPos.y, angle: targetCarPos.angle || 0 })
  }, [railCarPosition, geoData?.features.length, stageSize, cameraState, canvasOffset, yardOrientation])

  // "Locate on Map" — recompute camera from geoData when a focus
  // request arrives (requestId bump) or geoData (re)loads. Runs even
  // before the initial camera exists: the target camera is derived
  // from geometry, not from the current cameraState. No-op when
  // focusTarget is absent — handleResetView/map behavior unchanged.
  useEffect(() => {
    if (!focusTarget || !geoData) return

    const track = geoData.features
      .filter((f) => f.properties.type === 'track')
      .find((t) => t.id === focusTarget.trackId)
    if (!track) return

    // Track-fit camera: fixed "inspect" zoom derived from the track's
    // own bbox (same math as the reset baseline, single feature).
    const trackCam = initializeCameraState(
      calculateBoundingBox([track]),
      stageSize.width,
      stageSize.height
    )

    if (focusTarget.kind === 'car' && focusTarget.linearOffset != null) {
      // Resolve the car's canvas point under the track-fit camera, then
      // invert the orientation transform + projection to get lon/lat and
      // re-center there. Zoom stays at track-fit — a fixed inspect level,
      // independent of wherever the user was zoomed before.
      const zero: CanvasOffset = { x: 0, y: 0 }
      const clamped = Math.max(
        0,
        Math.min(focusTarget.linearOffset, track.properties?.lengthFeet || 2000)
      )
      const canvasPos = interpolateTrackPosition(
        track,
        clamped,
        trackCam,
        zero,
        stageSize.width,
        stageSize.height,
        yardOrientation
      )
      const unX =
        yardOrientation === 'REVERSED' ? stageSize.width - canvasPos.x : canvasPos.x
      const carGeo = canvasToCoordinates(
        unX,
        canvasPos.y,
        trackCam,
        zero,
        stageSize.width,
        stageSize.height
      )
      setCameraState({
        centerLon: carGeo.lon,
        centerLat: carGeo.lat,
        zoomScale: trackCam.zoomScale,
      })
    } else {
      setCameraState(trackCam)
    }
    // Center the projection — prior drag pans must not carry into focus.
    setCanvasOffset({ x: 0, y: 0 })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusTarget?.requestId, geoData])

  const handleResetView = () => {
    if (!geoData) return
    
    // Recalculate bounding box from current data to reset to static baseline
    const tracks = geoData.features.filter((f) => f.properties.type === 'track')
    const switches = geoData.features.filter((f) => f.properties.type === 'switch')
    const bbox = calculateBoundingBox([...tracks, ...switches])
    const initialCameraState = initializeCameraState(bbox, stageSize.width, stageSize.height)
    
    // Reset camera state to static baseline
    setCameraState(initialCameraState)
    // Reset canvas offset to origin
    setCanvasOffset({ x: 0, y: 0 })
    // Reset initialization flag to allow re-initialization if needed
    isMapInitialized.current = false
  }

  // Zoom handler with strict NaN/zero guardrails and pointer anchoring
  const handleWheel = (e: Konva.KonvaEventObject<WheelEvent>) => {
    if (!ENABLE_CAMERA_INTERACTIONS || !cameraState) return

    e.evt.preventDefault()

    const MIN_ZOOM = 50
    const MAX_ZOOM = 500000
    const ZOOM_THRESHOLD = 5

    const stage = stageRef.current
    if (!stage) return

    const pointerPos = stage.getPointerPosition()
    if (!pointerPos) return

    const deltaY = e.evt.deltaY

    if (Math.abs(deltaY) < ZOOM_THRESHOLD) {
      return
    }

    const now = Date.now()
    if (now - lastZoomTime.current < ZOOM_DEBOUNCE_MS) {
      return
    }
    lastZoomTime.current = now

    const mouseGeo = canvasToCoordinates(
      pointerPos.x,
      pointerPos.y,
      cameraState,
      canvasOffset,
      stageSize.width,
      stageSize.height
    )

    const zoomDirection = deltaY > 0 ? -1 : 1
    const zoomPercentage = 0.02
    const newScale = cameraState.zoomScale * (zoomDirection > 0 ? (1 + zoomPercentage) : (1 - zoomPercentage))

    if (isNaN(newScale) || newScale <= 0 || !isFinite(newScale)) {
      return
    }

    const clampedScale = Math.max(MIN_ZOOM, Math.min(newScale, MAX_ZOOM))

    const newCenterLon = mouseGeo.lon - (pointerPos.x - stageSize.width / 2 - canvasOffset.x) / clampedScale
    const newCenterLat = mouseGeo.lat + (pointerPos.y - stageSize.height / 2 - canvasOffset.y) / clampedScale

    if (isNaN(newCenterLon) || isNaN(newCenterLat) || !isFinite(newCenterLon) || !isFinite(newCenterLat)) {
      return
    }

    setCameraState({
      centerLon: newCenterLon,
      centerLat: newCenterLat,
      zoomScale: clampedScale
    })
  }

  // Pan handlers with feature flag guard
  const handleMouseDown = (e: Konva.KonvaEventObject<MouseEvent>) => {
    if (!ENABLE_CAMERA_INTERACTIONS) return
    setIsDragging(true)
    lastMousePos.current = { x: e.evt.clientX, y: e.evt.clientY }
  }

  const handleMouseMove = (e: Konva.KonvaEventObject<MouseEvent>) => {
    if (!ENABLE_CAMERA_INTERACTIONS || !isDragging || !lastMousePos.current) return

    const deltaX = e.evt.clientX - lastMousePos.current.x
    const deltaY = e.evt.clientY - lastMousePos.current.y

    setCanvasOffset(prev => ({
      x: prev.x + deltaX,
      y: prev.y + deltaY
    }))

    lastMousePos.current = { x: e.evt.clientX, y: e.evt.clientY }
  }

  const handleMouseUp = () => {
    if (!ENABLE_CAMERA_INTERACTIONS) return
    setIsDragging(false)
    lastMousePos.current = null
  }

  const handleMouseLeave = () => {
    if (!ENABLE_CAMERA_INTERACTIONS) return
    setIsDragging(false)
    lastMousePos.current = null
  }

  // ---------- Touch gestures: one-finger pan, two-finger pinch ----------
  // TODO(tablet-refactor): this whole section is the body of the future
  // useTouchCameraGestures hook (see touchGesture note above).
  // Positions are converted to stage-local coords via the container rect
  // (clientX/Y are viewport coords; canvasToCoordinates wants stage-space).
  const stageRect = () => stageRef.current?.container().getBoundingClientRect()

  const touchMidpoint = (t0: Touch, t1: Touch, rect: DOMRect) => ({
    x: (t0.clientX + t1.clientX) / 2 - rect.left,
    y: (t0.clientY + t1.clientY) / 2 - rect.top,
  })

  const touchDist = (t0: Touch, t1: Touch) =>
    Math.hypot(t0.clientX - t1.clientX, t0.clientY - t1.clientY)

  const handleTouchStart = (e: Konva.KonvaEventObject<TouchEvent>) => {
    if (!ENABLE_CAMERA_INTERACTIONS || !cameraState) return
    const touches = e.evt.touches
    const rect = stageRect()
    if (!rect) return
    const g = touchGesture.current
    g.moved = false
    if (touches.length === 2) {
      // preventDefault blocks the browser's native pinch-zoom/scroll and
      // the synthesized mouse events that would double-drive the camera.
      e.evt.preventDefault()
      const mid = touchMidpoint(touches[0], touches[1], rect)
      g.mode = 'pinch'
      g.pinchDist = touchDist(touches[0], touches[1])
      g.lastX = mid.x
      g.lastY = mid.y
    } else if (touches.length === 1) {
      g.mode = 'pan'
      g.lastX = touches[0].clientX - rect.left
      g.lastY = touches[0].clientY - rect.top
    }
  }

  const handleTouchMove = (e: Konva.KonvaEventObject<TouchEvent>) => {
    if (!ENABLE_CAMERA_INTERACTIONS || !cameraState) return
    const touches = e.evt.touches
    const rect = stageRect()
    if (!rect) return
    const g = touchGesture.current

    if (touches.length === 2) {
      e.evt.preventDefault()
      const mid = touchMidpoint(touches[0], touches[1], rect)
      const dist = touchDist(touches[0], touches[1])
      // First 2-finger frame: anchor the gesture without applying deltas.
      if (g.mode !== 'pinch' || g.pinchDist <= 0) {
        g.mode = 'pinch'
        g.pinchDist = dist
        g.lastX = mid.x
        g.lastY = mid.y
        return
      }

      // Pan component — midpoint drift pans the canvas.
      const dx = mid.x - g.lastX
      const dy = mid.y - g.lastY
      if (dx !== 0 || dy !== 0) {
        setCanvasOffset((prev) => ({ x: prev.x + dx, y: prev.y + dy }))
        g.moved = true
      }

      // Pinch component — same camera math as handleWheel, ratio-driven
      // and anchored at the midpoint. zoomScale = px per geo unit, so
      // spreading fingers (ratio > 1) zooms in.
      const ratio = dist / g.pinchDist
      if (isFinite(ratio) && ratio > 0 && Math.abs(ratio - 1) > 0.005) {
        const MIN_ZOOM = 50
        const MAX_ZOOM = 500000
        const clampedScale = Math.max(MIN_ZOOM, Math.min(cameraState.zoomScale * ratio, MAX_ZOOM))
        if (isFinite(clampedScale) && clampedScale !== cameraState.zoomScale) {
          const midGeo = canvasToCoordinates(
            mid.x, mid.y, cameraState, canvasOffset, stageSize.width, stageSize.height
          )
          const newCenterLon = midGeo.lon - (mid.x - stageSize.width / 2 - canvasOffset.x) / clampedScale
          const newCenterLat = midGeo.lat + (mid.y - stageSize.height / 2 - canvasOffset.y) / clampedScale
          if (isFinite(newCenterLon) && isFinite(newCenterLat)) {
            setCameraState({ centerLon: newCenterLon, centerLat: newCenterLat, zoomScale: clampedScale })
            g.moved = true
          }
        }
      }
      g.pinchDist = dist
      g.lastX = mid.x
      g.lastY = mid.y
    } else if (touches.length === 1 && g.mode === 'pan') {
      const x = touches[0].clientX - rect.left
      const y = touches[0].clientY - rect.top
      const dx = x - g.lastX
      const dy = y - g.lastY
      if (dx !== 0 || dy !== 0) {
        e.evt.preventDefault()
        setCanvasOffset((prev) => ({ x: prev.x + dx, y: prev.y + dy }))
        if (Math.abs(dx) + Math.abs(dy) > 0) g.moved = true
        g.lastX = x
        g.lastY = y
      }
    }
  }

  const handleTouchEnd = (e: Konva.KonvaEventObject<TouchEvent>) => {
    const g = touchGesture.current
    if (g.moved) {
      // Swallow the tap Konva fires on release — the gesture was a
      // pan/pinch, not a selection.
      suppressTapUntil.current = Date.now() + 350
    }
    const touches = e.evt.touches
    if (touches.length === 0) {
      g.mode = 'none'
      g.moved = false
    } else if (touches.length === 1) {
      // Pinch → single-finger transition: re-anchor so the camera doesn't
      // jump by the released finger's offset.
      const rect = stageRect()
      if (rect) {
        g.mode = 'pan'
        g.lastX = touches[0].clientX - rect.left
        g.lastY = touches[0].clientY - rect.top
      }
    }
  }

  // Track select goes through a suppression window so gesture-end taps
  // don't open the inspector.
  // TODO(tablet-refactor): moves into the useTouchCameraGestures hook as a
  // wrapped `onTap` guard — callers shouldn't have to know about the window.
  const handleTrackTap = (trackId: string) => {
    if (Date.now() < suppressTapUntil.current) return
    onTrackClick?.(trackId)
  }

  // Show blank state when no yard is selected or camera not initialized
  if (!activeFacilityId || !cameraState) {
    if (!activeFacilityId) {
      return (
        <div className="canvas-container" style={{ width: '100%', height: '100%', position: 'relative' }}>
          <div className="flex items-center justify-center" style={{ width: '100%', height: '100%' }}>
            <div className="text-center">
              <p className="text-lg font-medium mb-2 text-slate-200">
                Select a scenario for simulation
              </p>
              <p className="text-sm text-slate-400">
                Choose a scenario from the dropdown to begin
              </p>
            </div>
          </div>
        </div>
      )
    }
    return <div className="canvas-container" style={{ width: '100%', height: '100%' }} />
  }

  return (
    <div className="canvas-container" style={{ width: '100%', height: '100%', position: 'relative' }}>
      {/* Search-focus indicator — the "way back" after a search zoom.
          X resets the camera to the full-yard fit and clears the
          caller's mapFocus so the chip disappears too. */}
      {focusTarget && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10 flex items-center gap-1.5 pl-3 pr-1 py-1 rounded-md bg-bg-main/90 border border-border backdrop-blur-sm text-xs text-text-primary">
          <span>
            Focus: <span className="font-medium">{focusTarget.label ?? focusTarget.trackId}</span>
          </span>
          <button
            onClick={() => { handleResetView(); onFocusClear?.() }}
            className="p-0.5 rounded hover:bg-hover text-text-muted hover:text-text-primary transition-colors"
            title="Back to full yard"
          >
            <X size={14} />
          </button>
        </div>
      )}
      <button
        onClick={handleResetView}
        className="absolute top-4 right-4 z-10 flex items-center gap-1.5 px-2.5 py-1.5 bg-bg-main/80 hover:bg-hover backdrop-blur-sm rounded-md transition-colors border border-border text-xs text-text-primary"
        title="Fit full yard"
      >
        <Maximize size={14} />
        Fit full yard
      </button>
      <YardMapCanvas
        geoData={geoData}
        yardAssets={yardAssets}
        activeTheme={activeTheme}
        cameraState={cameraState}
        canvasOffset={canvasOffset}
        stageSize={stageSize}
        yardOrientation={yardOrientation}
        onTrackClick={handleTrackTap}
        activeSweepAudits={activeSweepAudits}
        onWheel={ENABLE_CAMERA_INTERACTIONS ? handleWheel : undefined}
        onMouseDown={ENABLE_CAMERA_INTERACTIONS ? handleMouseDown : undefined}
        onMouseMove={ENABLE_CAMERA_INTERACTIONS ? handleMouseMove : undefined}
        onMouseUp={ENABLE_CAMERA_INTERACTIONS ? handleMouseUp : undefined}
        onMouseLeave={ENABLE_CAMERA_INTERACTIONS ? handleMouseLeave : undefined}
        onTouchStart={ENABLE_CAMERA_INTERACTIONS ? handleTouchStart : undefined}
        onTouchMove={ENABLE_CAMERA_INTERACTIONS ? handleTouchMove : undefined}
        onTouchEnd={ENABLE_CAMERA_INTERACTIONS ? handleTouchEnd : undefined}
        isDragging={isDragging}
        railCarPosition={railCarPosition}
        currentPos={currentPos}
        railCarRef={railCarRef}
        hoveredCarId={hoveredCarId}
        setHoveredCarId={setHoveredCarId}
        alerts={alerts}
        enableContinuousScaling={ENABLE_CONTINUOUS_SCALING}
        stageRef={stageRef}
      />
    </div>
  )
}

export default React.memo(YardMap)
